import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, Building2, Calendar } from 'lucide-react';
import { authService } from '../../services/api';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [hasPendingBooking, setHasPendingBooking] = useState(false);
  const [pendingBookingId, setPendingBookingId] = useState<string | null>(null);
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) { setStatus('error'); setMessage('Token de validation manquant'); return; }
      try {
        await authService.verifyEmail(token);
        setStatus('success');
        setMessage('Votre email a été validé avec succès !');
        
        // Vérifier s'il y a une réservation en attente
        const storedBookingId = localStorage.getItem('pending_booking_id');
        if (storedBookingId) {
          setHasPendingBooking(true);
          setPendingBookingId(storedBookingId);
        }
      } catch (error: any) {
        setStatus('error');
        setMessage(error.response?.data?.error || 'Erreur lors de la validation');
      }
    };
    verifyEmail();
  }, [token]);

  const handleLoginClick = () => {
    // Ne pas effacer les données de réservation, elles seront utilisées après connexion
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full text-center">
        <Link to="/" className="inline-flex items-center space-x-2 mb-8">
          <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center">
            <Building2 className="w-7 h-7 text-white" />
          </div>
        </Link>
        <div className="card p-8">
          {status === 'loading' && (
            <div className="py-8">
              <Loader className="w-16 h-16 text-primary-500 mx-auto animate-spin" />
              <p className="mt-4 text-neutral-600">Validation en cours...</p>
            </div>
          )}
          {status === 'success' && (
            <div className="py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
              <h2 className="mt-4 text-2xl font-bold text-neutral-900">Email validé !</h2>
              <p className="mt-2 text-neutral-600">{message}</p>
              
              {hasPendingBooking && (
                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center justify-center gap-2 text-amber-800 font-medium mb-2">
                    <Calendar className="w-5 h-5" />
                    <span>Vous avez une réservation en attente</span>
                  </div>
                  <p className="text-amber-700 text-sm">
                    Connectez-vous pour accéder à votre espace personnel et procéder au paiement de votre réservation.
                  </p>
                </div>
              )}
              
              <Link 
                to="/login" 
                className="btn btn-primary mt-6 inline-block"
                onClick={handleLoginClick}
              >
                {hasPendingBooking ? 'Se connecter et finaliser ma réservation' : 'Se connecter'}
              </Link>
            </div>
          )}
          {status === 'error' && (
            <div className="py-8">
              <XCircle className="w-16 h-16 text-red-500 mx-auto" />
              <h2 className="mt-4 text-2xl font-bold text-neutral-900">Erreur</h2>
              <p className="mt-2 text-neutral-600">{message}</p>
              <Link to="/login" className="btn btn-outline mt-6 inline-block">Retour à la connexion</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default VerifyEmailPage;
